import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { CGTranslations, HeaderSelectOption } from '@ingenico/react-configuration-generator';
import { TargetEntityTypeEnum } from '@ingenico/react-configuration-generator';
import type { ParameterSets, Translations } from '@ingenico/react-form-builder';
import type { i18n } from 'i18next';
import { toString } from 'lodash-es';
import type { TFunction } from 'react-i18next';
import { hasMultiMerchantParameterSets } from './hasMultiMerchantParameterSets.util';

export function getConfigurationFormBuilderTranslations(
  t: TFunction,
  entityType: TargetEntityTypeEnum,
): CGTranslations {
  return {
    appsView: {
      addButton: t('formBuilder.appOrSystemPackageList.button.add', { entity: t('formBuilder.common.app') }),
      noSelectedApp: t('formBuilder.noSelectedApp.message'),
      title: t('formBuilder.appOrSystemPackageList.title', { entities: t('formBuilder.common.apps') }),
      getSelectedApps: (selectedAppCount: number) =>
        t('formBuilder.appOrSystemPackageList.selectedEntities', {
          entity: t('formBuilder.common.app'),
          count: selectedAppCount,
        }),
    },
    emptyAppOrSpView: {
      getAddAppButtonLabel: (isApp) =>
        t('formBuilder.emptyAppOrSystemPackage.button.add', {
          entities: isApp ? t('formBuilder.common.apps') : t('formBuilder.common.systemPackages'),
        }),
      getSubTitle: (isApp) =>
        t('formBuilder.emptyAppOrSystemPackage.subtitle', {
          entities: isApp ? t('formBuilder.common.apps') : t('formBuilder.common.systemPackages'),
        }),
      getTitle: (isApp) =>
        t('formBuilder.emptyAppOrSystemPackage.title', {
          entities: isApp ? t('formBuilder.common.apps') : t('formBuilder.common.systemPackages'),
        }),
    },
    osView: {
      title: t('formBuilder.osProfile.title'),
      fetchDataErrorMessage: t('formBuilder.osProfile.fetchDataErrorMessage'),
      fieldName: t('formBuilder.osProfile.fields.name'),
      fieldVersion: t('formBuilder.osProfile.fields.version'),
      goToOsDetails: t('formBuilder.osProfile.actions.goToOsDetails'),
      goToOsList: t('formBuilder.osProfile.actions.goToOsList'),
      option: t('formBuilder.osProfile.option'),
    },
    parameterTemplateView: {
      getBackButton: (isApp) =>
        t('formBuilder.parameterTemplate.button.backToAppOrSystemPackage', {
          entity: isApp ? t('formBuilder.common.app') : t('formBuilder.common.systemPackage'),
        }),
      fetchDataErrorMessage: t('formBuilder.parameterTemplate.fetchDataErrorMessage'),
      configNotValidError: t('error.default.fetch', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      loadingError: t('error.default.fetch', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      noInputsInfo: t('error.emptyConfiguration', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      changeDynamicOptionError: t('error.default.changeDynamicOptionError'),
    },
    rkiView: {
      fieldLabel: t('formBuilder.remoteKeyInjection.fieldLabel'),
      keysetsFieldLabel: t('formBuilder.remoteKeyInjection.keysetsFieldLabel'),
      keysetsWarningTitle: t('formBuilder.remoteKeyInjection.keysetsWarningTitle'),
      keysetsWarningMessage: t('formBuilder.remoteKeyInjection.keysetsWarningMessage'),
      title: t('formBuilder.remoteKeyInjection.title'),
      subtitle: t('formBuilder.remoteKeyInjection.subtitle'),
      tooltip: t('formBuilder.remoteKeyInjection.tooltip'),
      fetchDataErrorMessage: t('formBuilder.remoteKeyInjection.fetchDataErrorMessage'),
    },
    systemPackagesView: {
      addButton: t('formBuilder.appOrSystemPackageList.button.add', { entity: t('formBuilder.common.systemPackage') }),
      noSelectedSystemPackage: t('formBuilder.noSelectedSystemPackage.message'),
      title: t('formBuilder.appOrSystemPackageList.title', { entities: t('formBuilder.common.systemPackages') }),
      getSelectedSystemPackages: (selectedSystemPackageCount: number) =>
        t('formBuilder.appOrSystemPackageList.selectedEntities', {
          entity: t('formBuilder.common.systemPackage'),
          count: selectedSystemPackageCount,
        }),
    },
    addAppOrSpModal: {
      getTitle: (isApp) =>
        t('formBuilder.modal.addAppOrSp.title', {
          entity: t(isApp ? 'formBuilder.common.app' : 'formBuilder.common.systemPackage'),
        }),
      fetchDataErrorMessage: t('formBuilder.modal.addAppOrSp.fetchDataErrorMessage'),
      getNoMoreEntitiesTitle: (isApp, isConfigurationModule) =>
        t('formBuilder.modal.addAppOrSp.noMoreEntitiesTitle', {
          entities: t(
            isConfigurationModule
              ? 'formBuilder.common.configurationModules'
              : isApp
                ? 'formBuilder.common.apps'
                : 'formBuilder.common.systemPackages',
          ),
        }),
      getNoMoreEntitiesMessage: (isApp, isConfigurationModule) =>
        t('formBuilder.modal.addAppOrSp.noMoreEntitiesMessage', {
          entities: t(
            isConfigurationModule
              ? 'formBuilder.common.configurationModules'
              : isApp
                ? 'formBuilder.common.apps'
                : 'formBuilder.common.systemPackages',
          ),
        }),
      emptyTitle: t('formBuilder.modal.addAppOrSp.emptyTitle'),
      getSelectedItem: (isApp) =>
        t('formBuilder.modal.addAppOrSp.selectedItem', {
          entity: t(isApp ? 'formBuilder.common.app' : 'formBuilder.common.systemPackage'),
        }),
      addAction: t('formBuilder.button.addAppOrSp'),
      cancelAction: t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      gotItAction: t('formBuilder.button.gotIt'),
      radioSelect: {
        title: t('formBuilder.modal.addAppOrSp.radioSelect.title'),
        applicationChoice: t('capitalize', {
          value: t('formBuilder.common.apps', { ns: ALL_LNGS_NAMESPACES.CONFIGURATION_PROFILE }),
          ns: ALL_LNGS_NAMESPACES.COMMON,
        }),
        systemPackageChoice: t('capitalize', {
          value: t('formBuilder.common.systemPackages', { ns: ALL_LNGS_NAMESPACES.CONFIGURATION_PROFILE }),
          ns: ALL_LNGS_NAMESPACES.COMMON,
        }),
        configurationModuleAppChoice: t('formBuilder.modal.addAppOrSp.radioSelect.configurationModuleAppChoice'),
        configurationModuleSystemPackageChoice: t(
          'formBuilder.modal.addAppOrSp.radioSelect.configurationModuleSystemPackageChoice',
        ),
      },
    },
    buttons: {
      retry: t('button.retry', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      confirm: t('button.confirm', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      continue: t('button.continue', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      back: t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      save: t('button.save', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      close: t('button.close', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      cancel: t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      quit: t('button.quit', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      schedule: t('button.schedule', { ns: ALL_LNGS_NAMESPACES.COMMON }),
    },
    cancelConfigurationModal: {
      title: t('confirmation.cancel.title'),
      subtitle: t('confirmation.cancel.subtitle'),
    },
    appOrSpContainerView: {
      emptyMessage: t('formBuilder.appOrSystemPackageContainer.emptyMessage'),
      editParameters: t('formBuilder.appOrSystemPackageContainer.editParameters'),
      accessParameters: t('formBuilder.appOrSystemPackageContainer.accessParameters'),
      fetchDataErrorMessage: t('formBuilder.appOrSystemPackageContainer.fetchDataErrorMessage'),
      versionNumber: t('formBuilder.appOrSystemPackageContainer.versionNumber'),
      getRemoveItem: (isApp: boolean) =>
        t('formBuilder.appOrSystemPackageContainer.removeEntity', {
          entity: isApp ? t('formBuilder.common.app') : t('formBuilder.common.systemPackage'),
        }),
      selectVersion: t('formBuilder.appOrSystemPackageContainer.selectVersion'),
      getGoToDetail: (isApp: boolean) =>
        t('formBuilder.appOrSystemPackageContainer.goToDetail', {
          entity: isApp ? t('formBuilder.common.app') : t('formBuilder.common.systemPackage'),
        }),
      selectVersionErrorMessage: t('empty.appOrSystemPackageVersions.errorMessage'),
      selectVersionLoadingText: t('autocomplete.loadingText', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      noSelectVersionMessage: t('empty.appOrSystemPackageVersions.message'),
      versionWithModuleName: (versionNumber: string, configurationModuleName: string) =>
        t('formBuilder.appOrSystemPackageContainer.versionWithModuleName', { versionNumber, configurationModuleName }),
    },

    saveConfigurationModal: {
      getCreateSuccessNotification: (configName: string) =>
        t('formBuilder.modal.saveChanges.createSuccessNotification', {
          configurationName: configName,
        }),
      ReviewChangesSubTitle: t('formBuilder.modal.saveChanges.subtitle'),
      ReviewChangesTitle: t('formBuilder.modal.saveChanges.title'),
      updateErrorNotification: t('formBuilder.modal.saveChanges.updateErrorNotification'),
      updateSuccessNotification: t('formBuilder.modal.saveChanges.updateSuccessNotification'),
      addedAppOrSpWithoutVersion: t('formBuilder.modal.saveChanges.addedAppOrSpWithoutVersion'),
    },
    rootConfigView: {
      title: t(
        entityType === TargetEntityTypeEnum.CONFIG_PROFILE ? 'formBuilder.configProfileTitle' : 'formBuilder.dmTitle',
      ),
    },
    reviewChangesView: {
      title: t('formBuilder.modal.reviewChanges.title'),
      getRemovedStatus: (item: string) => t('formBuilder.modal.reviewChanges.status.removed', { entity: item }),
      getAppsOrSpRemovedStatus: (isApp) =>
        t('formBuilder.modal.reviewChanges.status.removed', {
          entity: isApp ? t('formBuilder.common.app') : t('formBuilder.common.systemPackage'),
        }),
      getAppsOrSpAddedStatus: (isApp) =>
        t('formBuilder.modal.reviewChanges.status.added', {
          entity: isApp ? t('formBuilder.common.app') : t('formBuilder.common.systemPackage'),
        }),
      previousStatus: t('formBuilder.modal.reviewChanges.status.previous'),
      getAddedStatus: (item: string) => t('formBuilder.modal.reviewChanges.status.added', { entity: item }),
      newValueStatus: t('formBuilder.modal.reviewChanges.status.newValue'),
      emptyValueStatus: t('formBuilder.modal.reviewChanges.status.emptyValue'),
      getModifiedStatus: (item: string) => t('formBuilder.modal.reviewChanges.status.modified', { entity: item }),
      getConfigurationModuleNameWithSoftwareName: (softwareName: string, configurationModuleName: string) =>
        t('formBuilder.modal.reviewChanges.configurationModuleNameWithSoftwareName', {
          softwareName,
          configurationModuleName,
        }),
      from: t('formBuilder.modal.reviewChanges.status.from'),
      to: t('formBuilder.modal.reviewChanges.status.to'),
      osSection: t('formBuilder.modal.reviewChanges.osSection'),
      appsSection: t('formBuilder.modal.reviewChanges.appsSection'),
      appConfigurationModuleSection: t('formBuilder.modal.reviewChanges.appConfigurationModuleSection'),
      systemPackageSection: t('formBuilder.modal.reviewChanges.systemPackageSection'),
      systemPackageConfigurationModuleSection: t(
        'formBuilder.modal.reviewChanges.systemPackageConfigurationModuleSection',
      ),
      rkiSection: t('formBuilder.modal.reviewChanges.rkiSection'),
      changedOsName: t('formBuilder.modal.reviewChanges.changes.osName'),
      changedOsVersion: t('formBuilder.modal.reviewChanges.changes.osVersion'),
      changedStatus: t('formBuilder.modal.reviewChanges.changes.status'),
      changedSelectedVersion: t('formBuilder.modal.reviewChanges.changes.version'),
      parameterTemplateSection: t('formBuilder.modal.reviewChanges.parameterTemplateSection'),
      cancel: t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      installOrderChanged: t('formBuilder.modal.reviewChanges.installOrderChanged'),
    },
    common: {
      autocompleteLoadingText: t('autocomplete.loadingText', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      getViewChanges: (changeCount: number) => `${t('formBuilder.footer.viewChanges')} (${toString(changeCount)})`,
      requiredError: t('error.required', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      deprecated: (item: string) => t('formBuilder.common.deprecated', { item }),
    },
    categorySelector: {
      os: t('navigation.page.os', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      apps: t('navigation.page.apps', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      systemPackages: t('navigation.page.systemPackages', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      rki: t('navigation.page.rki', { ns: ALL_LNGS_NAMESPACES.COMMON }),
    },
  };
}

export function getFormGeneratorTranslations(t: TFunction, i18n: i18n): Translations {
  return {
    tfunction: t,
    language: i18n.language,
    button: {
      edit: t('button.edit', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      delete: t('button.delete', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      addRow: t(`formGenerator.buttons.addRow`, { ns: ALL_LNGS_NAMESPACES.COMMON }),
      cancel: t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      save: t('button.save', { ns: ALL_LNGS_NAMESPACES.COMMON }),
    },
    namespaces: {
      defaultRenderColumnHeader: ALL_LNGS_NAMESPACES.ORGANIZATION,
    },
    boolean: {
      yes: t('field.boolean.yes', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      no: t('field.boolean.no', { ns: ALL_LNGS_NAMESPACES.COMMON }),
    },
    search: {
      placeholder: t('search.placeholder', { ns: ALL_LNGS_NAMESPACES.COMMON }),
    },
    grid: {
      nestedRowCount: (count: number) =>
        t('formGenerator.grid.nestedTableRowCount', { ns: ALL_LNGS_NAMESPACES.COMMON, count }),
    },
    title: {
      drawer: {
        edit: (title?: string) => t(`formGenerator.title.drawer.edit`, { ns: ALL_LNGS_NAMESPACES.COMMON, title }),
        add: (title?: string) => t(`formGenerator.title.drawer.add`, { ns: ALL_LNGS_NAMESPACES.COMMON, title }),
        view: (title?: string) => t(`formGenerator.title.drawer.view`, { ns: ALL_LNGS_NAMESPACES.COMMON, title }),
      },
    },
    error: {
      default: t('error.default', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      dynamic: (key: string) => t(`error.${key}`),
      invalidEmail: t('error.invalidEmail', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      invalidUrl: t('error.invalidUrl', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      invalidPhoneNumber: t('error.invalidPhoneNumber', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      required: t('error.required', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      pattern: t('error.pattern', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      uniqueKey: t('error.uniqueKeyDuplicate', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      uniqueKeyNotEmpty: t('error.uniqueKeyNotEmpty', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      max: (max?: number) => t('error.max', { ns: ALL_LNGS_NAMESPACES.COMMON, max }),
      min: (min?: number) => t('error.min', { ns: ALL_LNGS_NAMESPACES.COMMON, min }),
      maxLength: (maxLength?: number) => t('error.maxLength', { ns: ALL_LNGS_NAMESPACES.COMMON, maxLength }),
      minLength: (minLength?: number) => t('error.minLength', { ns: ALL_LNGS_NAMESPACES.COMMON, minLength }),
      tableMinRows: (min?: number) => t('error.tableMinRows', { ns: ALL_LNGS_NAMESPACES.COMMON, count: min }),
      tableMaxRows: (max?: number) => t('error.tableMaxRows', { ns: ALL_LNGS_NAMESPACES.COMMON, count: max }),
      match: (fieldName: string, regex: string) =>
        t('error.match', {
          ns: ALL_LNGS_NAMESPACES.COMMON,
          fieldName: t('field.name', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          regex,
        }),
    },
    emptyInputsBannerLabel: t('message.details.noParameters', { ns: ALL_LNGS_NAMESPACES.CONFIGURATION_PROFILE }),
    input: (input: string) => t(input, { ns: ALL_LNGS_NAMESPACES.COMMON }),
    createSmartField: {
      defaultValue: t('fields.defaultValue', { ns: ALL_LNGS_NAMESPACES.SMART_FIELDS }),
      descriptionLabel: t('fields.description.label', { ns: ALL_LNGS_NAMESPACES.SMART_FIELDS }),
      descriptionPlaceholder: t('fields.description.placeholder', { ns: ALL_LNGS_NAMESPACES.SMART_FIELDS }),
      nameLabel: t('fields.name.label', { ns: ALL_LNGS_NAMESPACES.SMART_FIELDS }),
      namePlaceholder: t('fields.name.placeholder', { ns: ALL_LNGS_NAMESPACES.SMART_FIELDS }),
      title: t('createFromParameterModal.title', { ns: ALL_LNGS_NAMESPACES.SMART_FIELDS }),
      categoriesLabel: t('fields.category', { ns: ALL_LNGS_NAMESPACES.SMART_FIELDS }),
      categoriesPlaceholder: t('fields.categories.placeholder', { ns: ALL_LNGS_NAMESPACES.SMART_FIELDS }),
      categoriesNoText: t('noOptions', { ns: ALL_LNGS_NAMESPACES.SMART_FIELDS }),
    },
    smartFieldPrefix: t('smartField.self', { ns: ALL_LNGS_NAMESPACES.COMMON }),
    noLabelDefined: t('smartField.noLabelDefined', { ns: ALL_LNGS_NAMESPACES.COMMON }),
    headerSelect: {
      label: t('headerSelect.label', { ns: ALL_LNGS_NAMESPACES.CONFIGURATION_PROFILE }),
      primaryLabel: t('headerSelect.primaryLabel', { ns: ALL_LNGS_NAMESPACES.CONFIGURATION_PROFILE }),
    },
    fieldStatus: {
      label: {
        undefined: t('fieldStatus.label.undefined', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        changed: t('fieldStatus.label.changed', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        mapped: t('fieldStatus.label.mapped', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      },
      tooltip: {
        defaultValue: {
          title: t('fieldStatus.tooltip.defaultValue.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.defaultValue.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        defaultEmptyValue: {
          title: t('fieldStatus.tooltip.defaultEmptyValue.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.defaultEmptyValue.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        defaultEmptyValueMinLength: {
          title: t('fieldStatus.tooltip.defaultEmptyValueMinLength.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.defaultEmptyValueMinLength.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        valueNotSet: {
          title: t('fieldStatus.tooltip.valueNotSet.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.valueNotSet.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        valueNotSetRequired: {
          title: t('fieldStatus.tooltip.valueNotSetRequired.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.valueNotSetRequired.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        valueNotSelected: {
          title: t('fieldStatus.tooltip.valueNotSelected.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.valueNotSelected.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        valueNotSelectedRequired: {
          title: t('fieldStatus.tooltip.valueNotSelectedRequired.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.valueNotSelectedRequired.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        referencesNotSelected: {
          title: t('fieldStatus.tooltip.referencesNotSelected.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.referencesNotSelected.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        resourceNotSelected: {
          title: t('fieldStatus.tooltip.resourceNotSelected.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.resourceNotSelected.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        resourceNotSelectedRequired: {
          title: t('fieldStatus.tooltip.resourceNotSelectedRequired.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.resourceNotSelectedRequired.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        changedValue: {
          title: t('fieldStatus.tooltip.changedValue.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.changedValue.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        changedEmptyValue: {
          title: t('fieldStatus.tooltip.changedEmptyValue.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.changedEmptyValue.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        changedTableValue: {
          title: t('fieldStatus.tooltip.changedTableValue.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.changedTableValue.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        changedFileValue: {
          title: t('fieldStatus.tooltip.changedFileValue.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.changedFileValue.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        changedDynamicChoiceValue: {
          title: t('fieldStatus.tooltip.changedDynamicChoiceValue.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.changedDynamicChoiceValue.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        changedReferences: {
          title: t('fieldStatus.tooltip.changedReferences.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.changedReferences.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        smartFieldValue: {
          title: t('fieldStatus.tooltip.smartFieldValue.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.smartFieldValue.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
        smartFieldTable: {
          title: t('fieldStatus.tooltip.smartFieldTable.title', { ns: ALL_LNGS_NAMESPACES.COMMON }),
          body: t('fieldStatus.tooltip.smartFieldTable.body', { ns: ALL_LNGS_NAMESPACES.COMMON }),
        },
      },
    },
  };
}

export function mapParameterSetsToHeaderSelectOptions(parameterSets: readonly ParameterSets[]): HeaderSelectOption[] {
  return parameterSets.reduce<HeaderSelectOption[]>(
    (options, { logicalDeviceId, logicalDeviceName, logicalDeviceRoleSnapshot }) => {
      if (!logicalDeviceId || options.some(({ value }) => value === logicalDeviceId)) {
        return options;
      }
      return [
        ...options,
        {
          value: logicalDeviceId,
          label: logicalDeviceName ?? logicalDeviceId,
          isPrimary: logicalDeviceRoleSnapshot === 'PRIMARY',
        },
      ];
    },
    [],
  );
}

export function getHeaderSelectOptions(
  parameterSets: ParameterSets[] | undefined,
  logicalDeviceId?: string,
  templateId?: string,
): readonly HeaderSelectOption[] {
  if (!logicalDeviceId || !hasMultiMerchantParameterSets(parameterSets, logicalDeviceId, templateId)) {
    return [];
  }
  const templateParameterSets = templateId
    ? parameterSets?.filter((parameterSet) => parameterSet.templateId === templateId)
    : parameterSets;
  // TODO: replace with `mapParameterSetsToHeaderSelectOptions(<second endpoint>.parameterSets)` once it exists.
  return mapParameterSetsToHeaderSelectOptions(templateParameterSets ?? []);
}

export function getSelectedDeviceParameterSets(
  parameterSets: ParameterSets[] | undefined,
  selectedLogicalDeviceId?: string,
): ParameterSets[] | undefined {
  if (!hasMultiMerchantParameterSets(parameterSets, selectedLogicalDeviceId)) {
    return parameterSets;
  }
  return parameterSets?.filter(({ logicalDeviceId }) => logicalDeviceId === selectedLogicalDeviceId);
}
